import { defineStore } from "pinia";
import { generateUid } from "@/utils/uid";
import { useCookie } from "@/composable/useCookie";
import { getShamsiDate } from "@/utils/helpers/dateTimeFormatter";
import { useToastStore } from "@/stores/toast";
import {
  convertDateToUnix,
  convertUnixToDate,
} from "~/utils/helpers/dateTimeFormatter";

const baseUrl = import.meta.env["VITE_SMS_URL"];

export const useSmsStore = defineStore("SmsStore", {
  state: () => ({
    tableDataFilter: {
      from_timestamp: "",
      to_timestamp: "",
      from_schedule_timestamp: "",
      to_schedule_timestamp: "",
      search: "",
      status: null,
      type: null,
    },
    receiveTableDataFilterForm: {
      from_timestamp: "",
      to_timestamp: "",
      search: "",
    },
    row: 1,
    sendInitialState: false,
    receiveInitialState: false,
    defaultMsg: [] as any[],
    defaultSubject: [] as any[],
    subjectError: "" as string,
    msgError: "" as string,
    sourcePhones: [] as any[],
    lastFileUplodedName: "",
    fileNamePreview: "",
    tableData: [] as any[],
    receiveTableData: [] as any[],
    loading: false,
    lastCursor: null,
    statusFilter: null,
    editDataForm: null,
    cost: null,
    phoneNumberList: [],
    form: {
      subject: "",
      message: "",
      receivers: {
        to_contact: [],
        to_group: [],
        to_raw_number: [],
      },
      src_phone: "",
      timestamp: 0,
    },
    time: "00:00",
  }),
  actions: {
    async defaultSubjectList() {
      const res = await $fetch<{ data: any[] }>(
          "/api/sms/default/subject/list"
      );
      this.defaultSubject = res.data;
    },
    async addSubject(subject: string) {
      let subjectList: string[] = [];
      await this.defaultSubjectList();
      for (let key of this.defaultSubject) {
        subjectList.push(key.subject);
      }
      if (subjectList.includes(subject)) {
        this.subjectError = "این مورد قبلا اضافه شده";
      } else {
        this.subjectError = "";
        await $fetch("/api/sms/default/subject/add", {
          method: "POST",
          body: { subject },
        });
      }
    },
    async removeDefaultSubject(id: number) {
      await $fetch("/api/sms/default/subject/delete", {
        method: "POST",
        body: { id: id.toString() },
      });
      await this.defaultSubjectList();
    },
    async defaultMsgList() {
      const res = await $fetch<{ data: any[] }>("/api/sms/default/msg/list");
      this.defaultMsg = res.data;
    },
    async addMsg(msg: any) {
      let msgList: string[] = [];
      await this.defaultMsgList();
      for (let key of this.defaultMsg) {
        msgList.push(key.message);
      }
      if (msgList.includes(msg)) {
        this.msgError = "این مورد قبلا اضافه شده";
      } else {
        this.msgError = "";
        await $fetch("/api/sms/default/msg/add", {
          method: "POST",
          body: { message: msg },
        });
      }
    },
    async removeDefaultMessage(id: number) {
      await $fetch("/api/sms/default/msg/delete", {
        method: "POST",
        body: { id: id.toString() },
      });
      await this.defaultMsgList();
    },
    async sourcePhoneList() {
      const res = await $fetch<{ data: any[] }>("/api/sms/srcPhone/list");
      this.sourcePhones = res.data;
    },
    async sendSms(form: any,Redirect: boolean,location: string) {
      const toastStore = useToastStore();
      const res: any = await $fetch("/api/sms/send", {
        method: "POST",
        body: form,
      });
      if (res.status == 200) {
        toastStore.showAlert({
          title: "با موفقیت ارسال شد",
          showCloseButton: true,
          timerProgressBar: true,
          type: "success",
          onClose: async () => {
            if (Redirect) {
              window.location.href = location;
            }
          },
        });
        this.time = "00:00";
        return true;
      } else {
        toastStore.showAlert({
          title: res.message,
          showCloseButton: true,
          timerProgressBar: true,
          timeout: 10000,
          type: "error",
        });
        return false;
      }
    },
    async upload(file: File) {
      const { getCookie } = useCookie();
      this.lastFileUplodedName = `${generateUid(30)}.${file.name
          .split(".")
          .pop()}`;
      await $fetch(`${baseUrl}/upload/${this.lastFileUplodedName}`, {
        method: "POST",
        body: file,
        headers: {
          Authorization: `Bearer ${getCookie("accessToken")}`,
          "Content-Length": file.size.toString(),
          "Content-Type":
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      });
    },
    async getTableData(cursor: string, form: any) {
      this.loading = true;
      if (!cursor) this.row = 1;
      const body = {
        cursor,
        from_timestamp: form.from_timestamp
            ? Number(convertDateToUnix(form.from_timestamp))
            : 0,
        to_timestamp: form.to_timestamp
            ? Number(convertDateToUnix(form.to_timestamp,true))
            : Number(convertDateToUnix()),
        from_schedule_timestamp: form.from_schedule_timestamp
            ? Number(convertDateToUnix(form.from_schedule_timestamp))
            : null,
        to_schedule_timestamp: form.to_schedule_timestamp
            ? Number(convertDateToUnix(form.to_schedule_timestamp, true))
            : null,
        status: form.status,
        search: form.search,
        type: "outbound",
      };

      const res: any = await $fetch("/api/sms/jobs/list", {
        method: "POST",
        body,
      });

      this.lastCursor = res.data.cursor;
      const result = res.data.messages.map((element: any) => ({
        ...element,
        row: this.row++,
        src_phone: {
          value: element.src_phone,
          color: "#E9E1F4",
          width: "100px",
          textColor: "#7543B9",
          border: "1px solid #BAA1DD",
        },
        message: element.message,
        meta_data: generateMetaData(
            element.meta_data?JSON.parse(element.meta_data):'-',
            element.navatel_send_type
        ),
        messageView:
            element.message.length > 20
                ? element.message.slice(0, 20) + "..."
                : element.message,
        subject: element.subject.slice(0, 10),
        time: getShamsiDate(element.create_time, true),
        job_timestamp: getShamsiDate(convertUnixToDate(element.job_timestamp, true),true),
        scheduleTime:
            element.job_timestamp != 0
                ? getShamsiDate(
                    convertUnixToDate(element.job_timestamp, true, false),
                    true
                )
                : getShamsiDate(element.create_time, true),
        unixTime: element.time,
        status: GenerateStatus(element.status),
        cancel_time: getShamsiDate(element.cancel_time),
        progressBar: GenerateProgressBar({
          successful_count: element.successful_count,
          total_count: element.total_count,
        }),
        type: tarnslateTypeSms(element.type, true),
        serviceType: element.type,
        recipients: {
          total_count: element.total_count,
          successful_count: element.successful_count,
        },
      }));

      this.tableData.push(...result);
      this.loading = false;
      this.sendInitialState = true;
    },
    async getReceiveTableData(cursor: string, form: any) {
      this.loading = true;
      if (!cursor) this.row = 1;
      const body = {
        cursor,
        from_timestamp: form.from_timestamp
            ? Number(convertDateToUnix(form.from_timestamp))
            : null,
        to_timestamp: form.to_timestamp
            ? Number(convertDateToUnix(form.to_timestamp,true))
            : null,
        search: form.search,
        message_id: form.message_id,
      };

      const res: any = await $fetch("/api/sms/jobs/inbounds", {
        method: "POST",
        body,
      });
      this.lastCursor = res.data.cursor;
      const result = res.data.messages.map((element: any) => ({
        ...element,
        row: this.row++,
        time: getShamsiDate(element.create_time, true),
        message: element.message,
        phone: {
          value: element.phone,
          color: "#E9E1F4",
          width: "100px",
          textColor: "#7543B9",
          border: "1px solid #BAA1DD",
        },
        src_phone: {
          value: element.src_phone,
          color: "#ECFBCE",
          width: "100px",
          textColor: "#67A228",
          border: "1px solid #BFF071",
        },
        ulid: element.ulid,
      }));

      this.receiveTableData.push(...result);
      this.loading = false;
      this.receiveInitialState = true;
    },
    async cancel(id: number) {
      await $fetch(`/api/sms/jobs/cancel`, {
        method: "POST",
        body: { id },
      });
      this.tableData = [];
      await this.getTableData("", this.tableDataFilter);
    },
    async retry(id: number) {
      await $fetch(`/api/sms/jobs/retry`, {
        method: "POST",
        body: { id },
      });
      this.tableData = [];
      await this.getTableData("", this.tableDataFilter);
    },
    async editJobMessage(param: any) {
      await $fetch("/api/sms/jobs/edit", {
        method: "POST",
        body: param,
      });
    },
    async getCost(data: any) {
      const res: any = await $fetch("/api/sms/cost", {
        method: "POST",
        body: data,
      });
      this.cost = Math.round(res?.data.cost * 10);
    },
  },
});

export const GenerateStatus = (status: number) => {
  const statusMapping: Record<
      number,
      {
        value: string;
        color: string;
        textColor: string;
        border: string;
        statusCode: number | null;
      }
  > = {
    0: {
      value: "ارسال شده",
      color: "#E3F8ED",
      textColor: "#4BD08B",
      border: "1px solid #A6E8C6",
      statusCode: 0,
    },
    1: {
      value: "آماده ارسال",
      color: "#FEF4E7",
      textColor: "#F49820",
      border: "1px solid #FCE0BB",
      statusCode: 1,
    },
    3: {
      value: "لغو شده",
      color: "#FEEFEB",
      textColor: "#F84C20",
      border: "1px solid #FDCBBF",
      statusCode: 3,
    },
    4: {
      value: "در حال ارسال",
      color: "#DFF6FC",
      textColor: "#46CAEB",
      border: "1px solid #A3E5F5",
      statusCode: 4,
    },
    default: {
      value: "---",
      color: "#fff",
      textColor: "#F84C20",
      border: "1px solid #fff",
      statusCode: null,
    },
  };

  return statusMapping[status] || statusMapping.default;
};

export const GenerateProgressBar = (item: {
  successful_count: number;
  total_count: number;
}) => {
  return (item.successful_count * 100) / item.total_count;
};

export const tarnslateTypeSms = (type: string) => {
  switch (type) {
    case "outbound":
      return {
        value: "ارسالی",
        color: "#E3F8ED",
        width: "80px",
        textColor: "#4BD08B",
        border: "1px solid #4BD08B",
      };
    case "inbound":
      return {
        value: "دریافتی",
        color: "#E9E1F4",
        width: "80px",
        textColor: "#8C62C6",
        border: "1px solid #8C62C6",
      };
    default:
      return {
        value: "---",
        color: "#ccebff",
        width: "80px",
        textColor: "#0085db",
        border: "1px solid #6bc4ff",
      };
  }
};

export const generateMetaData = (arrayData:any, sendType: Number) => {
  switch (sendType) {
    case 1:
      return {
        value: arrayData,
        color: "#fff",
        width: "80px",
        textColor: "#151515",
        border: "1px solid #ffffff",
      };
    case 2:
      return {
        value: arrayData,
        color: "#FEF2CA",
        width: "auto",
        textColor: "#D87520",
        border: "1px solid #FCD15C",
      };
    case 3:
      return {
        value: arrayData,
        color: "#FCE7F3",
        width: "80px",
        textColor: "#D92876",
        border: "1px solid #F8A9D3",
      };
    default:
      return {
        value: arrayData?arrayData:'-',
        color: "#fff",
        width: "80px",
        textColor: "#151515",
        border: "1px solid #ffffff",
      };
  }
};

